import { MyProjects, Users } from '@platform/client';
import { ProfessionalType } from '@platform/client/schemas';
import { IconProps } from '@platform/components';
import EVENTS from '../../analytics/events';
import {
  DEFAULT_HELP_LINK,
  ORG_DEFAULT_FAQ_LINK,
  ORG_TSH_FAQ_LINK,
} from '../../constants/dashboard';
import { ROUTES } from '../../routes/consts';
import {
  isCorporateProNoCohort,
  isInactiveUser,
  isNonCorporatePro,
} from '../../util/ProgramHelpers';

export type NavItemConfig = {
  id: string;
  title: string;
  href: string;
  iconType: IconProps['type'];
  viewbox?: string;
  linkType: 'internal' | 'external';
  newTab?: boolean;
  eventName: string;
  /** A count to render as a pill badge on this item, e.g. outstanding My Projects action items */
  badge?: number;
  externalIndicator?: boolean;
};

export type NavItems = {
  isLoading: boolean;
  mainItems: NavItemConfig[];
  footerItems: NavItemConfig[];
};

const PROFESSIONAL_DEFAULT_RESOURCES_LINK = '/professional-learning-resources';
const PROFESSIONAL_DEFAULT_FAQ_LINK =
  'https://zsupport.movingworlds.org/portal/en/kb/movingworlds-faqs/professional-faqs';

const professionalResourcesLinks: Record<ProfessionalType, string | null> = {
  ey: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  f5: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  generic: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  kering: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  mwi: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  paypal: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  reckitt: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  sap: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  tsh: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  lumen: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
  wmp: null,
  philips: PROFESSIONAL_DEFAULT_RESOURCES_LINK,
};

const professionalFaqLinks: Record<ProfessionalType, string | null> = {
  ey: 'https://movingworlds.org/partner/ey/ey-coaches',
  f5: PROFESSIONAL_DEFAULT_FAQ_LINK,
  generic: PROFESSIONAL_DEFAULT_FAQ_LINK,
  kering: PROFESSIONAL_DEFAULT_FAQ_LINK,
  mwi: PROFESSIONAL_DEFAULT_FAQ_LINK,
  paypal:
    'https://support.movingworlds.org/hc/en-us/categories/360005828313-PayPal-Gives-Nonprofit-Experience-Program',
  reckitt: PROFESSIONAL_DEFAULT_FAQ_LINK,
  sap: 'https://movingworlds.org/partner/sap/acceleration-collective-employees#frequently-asked-questions',
  tsh: 'https://zsupport.movingworlds.org/portal/en/kb/movingworlds-faqs/professional-faqs/transform-support-hub-faqs-for-professionals',
  wmp: PROFESSIONAL_DEFAULT_FAQ_LINK,
  lumen: PROFESSIONAL_DEFAULT_FAQ_LINK,
  philips: PROFESSIONAL_DEFAULT_FAQ_LINK,
};

const DASHBOARD_BASE = '~/platform' + ROUTES.DASHBOARD;

const homeItem = (): NavItemConfig => ({
  id: 'home',
  title: 'Home',
  href: DASHBOARD_BASE,
  iconType: 'home-line',
  viewbox: '0 0 20 20',
  linkType: 'internal',
  eventName: EVENTS.DASHBOARD.LEFT_NAV.DASHBOARD_CLICKED,
});

const myProjectsItem = (count: number | undefined): NavItemConfig => ({
  id: 'my-projects',
  title: 'My Projects',
  href: DASHBOARD_BASE + ROUTES.MY_PROJECTS,
  iconType: 'folder-check',
  linkType: 'internal',
  eventName: EVENTS.DASHBOARD.LEFT_NAV.MY_PROJECTS_CLICKED,
  badge: count && count > 0 ? count : undefined,
});

const accountSettingsItem = (): NavItemConfig => ({
  id: 'account-settings',
  title: 'Account Settings',
  href: DASHBOARD_BASE + ROUTES.ACCOUNT,
  iconType: 'settings-02',
  linkType: 'internal',
  eventName: EVENTS.DASHBOARD.LEFT_NAV.ACCOUNT_SETTINGS_CLICKED,
});

const askAQuestionItem = (): NavItemConfig => ({
  id: 'ask-a-question',
  title: 'Ask us a question',
  href: DEFAULT_HELP_LINK,
  iconType: 'mail-01',
  linkType: 'external',
  eventName: EVENTS.DASHBOARD.LEFT_NAV.ASK_US_A_QUESTION_CLICKED,
});

const useNavItems = (): NavItems => {
  const { isSuccess, data: user } = Users.useGetCurrentUser();
  const { data: count } = MyProjects.useCountUserActionItems();

  if (!isSuccess || !user) {
    return { isLoading: true, mainItems: [], footerItems: [] };
  }

  const footerItems = [accountSettingsItem(), askAQuestionItem()];

  if (user.accountType === 'organization') {
    const faqLink =
      user.organization?.applicationStatus === 'approved' ? ORG_TSH_FAQ_LINK : ORG_DEFAULT_FAQ_LINK;

    const mainItems: NavItemConfig[] = [
      homeItem(),
      myProjectsItem(count),
      {
        id: 'create-project',
        title: 'Create Project',
        href: '~/platform' + ROUTES.CREATE_PROJECT,
        iconType: 'file-plus-02',
        linkType: 'internal',
        eventName: EVENTS.DASHBOARD.LEFT_NAV.CREATE_PROJECT_CLICKED,
      },
      {
        id: 'browse-professionals',
        title: 'Browse Professionals',
        href: '~/platform' + ROUTES.USER_SEARCH,
        iconType: 'search-sm',
        linkType: 'internal',
        eventName: EVENTS.DASHBOARD.LEFT_NAV.FIND_PROFESSIONALS_CLICKED,
      },
      {
        id: 'community-updates',
        title: 'Community Updates',
        href: DASHBOARD_BASE + ROUTES.COMMUNITY,
        iconType: 'calendar-heart',
        viewbox: '0 0 16.67 18.34',
        linkType: 'internal',
        eventName: EVENTS.DASHBOARD.LEFT_NAV.COMMUNITY_CLICKED,
      },
      {
        id: 'faq',
        title: 'FAQ',
        href: faqLink,
        iconType: 'help-circle',
        linkType: 'external',
        externalIndicator: true,
        eventName: EVENTS.DASHBOARD.LEFT_NAV.FAQ_CLICKED,
      },
    ];

    return { isLoading: false, mainItems, footerItems };
  }

  if (user.accountType !== 'experteer') {
    // Admins and corporate admins don't use this sidebar (admins have their own layout;
    // corporate admins land on CorporateDashboardPage, which doesn't render this component).
    return { isLoading: false, mainItems: [], footerItems };
  }

  const blockSearch =
    isNonCorporatePro(user) || isInactiveUser(user) || isCorporateProNoCohort(user);
  const resourcesLink = professionalResourcesLinks[user.professionalType] ?? null;
  const faqLink = professionalFaqLinks[user.professionalType] ?? '';

  const mainItems: NavItemConfig[] = [homeItem(), myProjectsItem(count)];

  if (!blockSearch) {
    mainItems.push({
      id: 'browse-projects',
      title: 'Browse projects',
      href: '~/platform' + ROUTES.SEARCH_PROJECTS,
      iconType: 'search-sm',
      linkType: 'internal',
      eventName: EVENTS.DASHBOARD.LEFT_NAV.FIND_PROJECTS_CLICKED,
    });
    mainItems.push({
      id: 'community-updates',
      title: 'Community Updates',
      href: DASHBOARD_BASE + ROUTES.COMMUNITY,
      iconType: 'calendar-heart',
      viewbox: '0 0 16.67 18.34',
      linkType: 'internal',
      eventName: EVENTS.DASHBOARD.LEFT_NAV.COMMUNITY_CLICKED,
    });
    if (user.cohort?.cohortId) {
      mainItems.push({
        id: 'my-cohort',
        title: 'My Cohort',
        href: DASHBOARD_BASE + ROUTES.MY_COHORT,
        iconType: 'users-01',
        linkType: 'internal',
        eventName: EVENTS.DASHBOARD.LEFT_NAV.MY_COHORT_CLICKED,
      });
    }
    if (resourcesLink) {
      mainItems.push({
        id: 'resources',
        title: 'Resources',
        href: resourcesLink,
        iconType: 'book-open-01',
        linkType: 'external',
        newTab: true,
        eventName: EVENTS.DASHBOARD.LEFT_NAV.RESOURCES_CLICKED,
      });
    }
  }

  if (faqLink) {
    mainItems.push({
      id: 'faq',
      title: 'FAQ',
      href: faqLink,
      iconType: 'help-circle',
      linkType: 'external',
      externalIndicator: true,
      eventName: EVENTS.DASHBOARD.LEFT_NAV.FAQ_CLICKED,
    });
  }

  return { isLoading: false, mainItems, footerItems };
};

export default useNavItems;
